import React, { useCallback, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BotStatusResponse } from '../types';
import BotsLogsModal from './BotsLogsModal';
import { useBackend } from '../hooks/useBackend';
import './BotStatus.css';

interface Experiment {
  experimentId: number;
  experimentName: string;
  experimentStartTime: string | null;
  experimentEndTime: string | null;
  groupIds: number[];
}

interface BotInfo {
  group_id: number;
  group_name: string;
  container_name: string;
  is_running: boolean;
  status: string;
  id?: string | null;
  image?: string | null;
  created?: string | null;
  users_count?: number | null;
}

const BotStatus: React.FC = () => {
  const [actionError, setActionError] = useState<string | null>(null);
  const [userCounts, setUserCounts] = useState<Record<string, number>>({});
  const [logsModal, setLogsModal] = useState<{ isOpen: boolean; groupId: string; containerName: string }>({
    isOpen: false,
    groupId: '',
    containerName: ''
  });
  const [collapsedExperiments, setCollapsedExperiments] = useState<Set<number>>(new Set());
  const [expandedBots, setExpandedBots] = useState<Set<string>>(new Set());
  const [addToExperimentModal, setAddToExperimentModal] = useState<{ isOpen: boolean; groupId: number; groupName: string }>({
    isOpen: false,
    groupId: 0,
    groupName: ''
  });
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [bulkActionInProgress, setBulkActionInProgress] = useState<'start' | 'stop' | null>(null);
  const queryClient = useQueryClient();
  const backend = useBackend();

  const fetchBotStatus = useCallback(async (): Promise<BotStatusResponse> => {
    return backend.getBotStatus() as Promise<BotStatusResponse>;
  }, [backend]);

  const fetchPersonaGroups = useCallback(async () => {
    return backend.getPersonaGroups(false);
  }, [backend]);

  const fetchExperiments = useCallback(async () => {
    return backend.getExperiments();
  }, [backend]);

  const startBot = useCallback(async (groupId: string, usersCount: number = 1) => {
    return backend.startBot(groupId, usersCount);
  }, [backend]);

  const stopBot = useCallback(async (groupId: string) => {
    return backend.stopBot(groupId);
  }, [backend]);

  const { data: botData, isLoading: loading, error, refetch } = useQuery<BotStatusResponse, Error>({
    queryKey: ['botStatus'],
    queryFn: fetchBotStatus,
    refetchInterval: 30000,
  });

  const { data: personaGroups } = useQuery({
    queryKey: ['personaGroups'],
    queryFn: fetchPersonaGroups,
    refetchInterval: 60000,
  });

  const { data: experiments } = useQuery({
    queryKey: ['experiments'],
    queryFn: fetchExperiments,
    refetchInterval: 60000,
  });

  // Group bots by experiment - using array to preserve backend order
  const { experimentBotsList, standaloneBots } = useMemo(() => {
    if (!botData?.bots || !experiments) {
      return { experimentBotsList: [], standaloneBots: botData?.bots || {} };
    }

    const experimentGroupIds = new Set<number>();
    const expBotsMap = new Map<number, { experiment: Experiment; bots: Record<string, BotInfo> }>();

    // Iterate in order from backend (already sorted by creation desc)
    (experiments as Experiment[]).forEach((exp) => {
      exp.groupIds.forEach((gid) => experimentGroupIds.add(gid));
      expBotsMap.set(exp.experimentId, { experiment: exp, bots: {} });
    });

    const standalone: Record<string, BotInfo> = {};

    Object.entries(botData.bots).forEach(([groupId, bot]) => {
      const gid = parseInt(groupId);
      let foundExp: Experiment | null = null;
      
      (experiments as Experiment[]).forEach((exp) => {
        if (exp.groupIds.includes(gid)) {
          foundExp = exp;
        }
      });

      if (foundExp) {
        const entry = expBotsMap.get((foundExp as Experiment).experimentId);
        if (entry) {
          entry.bots[groupId] = bot;
        }
      } else {
        standalone[groupId] = bot;
      }
    });

    // Convert Map to array to preserve insertion order
    return { experimentBotsList: Array.from(expBotsMap.values()), standaloneBots: standalone };
  }, [botData, experiments]);

  const groupUserCounts = useMemo(() => {
    if (!personaGroups || !Array.isArray(personaGroups)) return {};
    
    const counts: Record<string, number> = {};
    personaGroups.forEach((group: any) => {
      if (group && (group.groupId !== undefined && group.groupId !== null)) {
        counts[group.groupId.toString()] = group.userCount || 0;
      } else if (group && (group.group_id !== undefined && group.group_id !== null)) {
        counts[group.group_id.toString()] = group.user_count || 0;
      }
    });
    
    return counts;
  }, [personaGroups]);

  const startBotMutation = useMutation({
    mutationFn: ({ groupId, usersCount }: { groupId: string; usersCount: number }) => 
      startBot(groupId, usersCount),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const stopBotMutation = useMutation({
    mutationFn: (groupId: string) => stopBot(groupId),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const deleteExperimentMutation = useMutation({
    mutationFn: (experimentId: number) => backend.deleteExperiment(experimentId),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['experiments'] });
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const removeGroupFromExperimentMutation = useMutation({
    mutationFn: ({ experimentId, groupId }: { experimentId: number; groupId: number }) =>
      backend.removeGroupsFromExperiment(experimentId, [groupId]),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['experiments'] });
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const addGroupToExperimentMutation = useMutation({
    mutationFn: ({ experimentId, groupId }: { experimentId: number; groupId: number }) =>
      backend.addGroupsToExperiment(experimentId, [groupId]),
    onSuccess: () => {
      setActionError(null);
      setAddToExperimentModal({ isOpen: false, groupId: 0, groupName: '' });
      queryClient.invalidateQueries({ queryKey: ['experiments'] });
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
    },
    onError: (error: Error) => {
      setActionError(error.message);
    },
  });

  const getStatusColor = useCallback((isRunning: boolean): string => {
    return isRunning ? '#4CAF50' : '#f44336';
  }, []);

  const formatDate = useCallback((dateString: string | null): string => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleString();
  }, []);

  const getUserCount = useCallback((groupId: string): number => {
    if (userCounts[groupId] !== undefined) {
      return userCounts[groupId];
    }
    return groupUserCounts[groupId] || 1;
  }, [userCounts, groupUserCounts]);

  const getGroupUserCount = useCallback((groupId: string): number => {
    return groupUserCounts[groupId] || 0;
  }, [groupUserCounts]);

  const setUserCount = useCallback((groupId: string, count: number) => {
    setUserCounts((prev: Record<string, number>) => ({ ...prev, [groupId]: count }));
  }, []);

  const handleStartBot = useCallback((groupId: string) => {
    setActionError(null);
    const usersCount = getUserCount(groupId);
    startBotMutation.mutate({ groupId, usersCount });
  }, [startBotMutation, getUserCount]);

  const handleStopBot = useCallback((groupId: string) => {
    setActionError(null);
    stopBotMutation.mutate(groupId);
  }, [stopBotMutation]);

  const toggleGroupSelection = useCallback((groupId: string) => {
    setSelectedGroups((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const selectAllGroups = useCallback((bots: Record<string, BotInfo>) => {
    setSelectedGroups(new Set<string>(Object.keys(bots)));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedGroups(new Set<string>());
  }, []);

  const handleBulkStart = useCallback(async () => {
    if (selectedGroups.size === 0) return;
    setBulkActionInProgress('start');
    setActionError(null);
    try {
      const allBots: Record<string, BotInfo> = botData?.bots || {};
      const groupIdsToUsersCount: Record<number, number> = {};
      Array.from(selectedGroups)
        .filter(gid => !allBots[gid]?.is_running && (groupUserCounts[gid] || 0) > 0)
        .forEach(gid => { groupIdsToUsersCount[parseInt(gid)] = getUserCount(gid); });

      if (Object.keys(groupIdsToUsersCount).length === 0) {
        setActionError('No eligible groups to start (all running or no personas)');
        return;
      }

      const result: any = await backend.bulkStartBots(groupIdsToUsersCount);
      if (result.summary?.failed > 0) {
        const failedGroups = Object.entries(result.results || {})
          .filter(([_, r]: [string, any]) => !r.success)
          .map(([gid, r]: [string, any]) => `Group ${gid}: ${r.message}`)
          .join('; ');
        setActionError(`${result.summary.failed} group(s) failed to start: ${failedGroups}`);
      } else {
        setSelectedGroups(new Set());
      }
    } catch (err: any) {
      const message = err?.body?.detail || err?.message || 'Bulk start failed';
      setActionError(String(message));
    } finally {
      setBulkActionInProgress(null);
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
    }
  }, [selectedGroups, botData, groupUserCounts, getUserCount, backend, queryClient]);

  const handleBulkStop = useCallback(async () => {
    if (selectedGroups.size === 0) return;
    setBulkActionInProgress('stop');
    setActionError(null);
    try {
      const allBots: Record<string, BotInfo> = botData?.bots || {};
      const groupIds = Array.from(selectedGroups)
        .filter(gid => allBots[gid]?.is_running)
        .map(gid => parseInt(gid));

      if (groupIds.length === 0) {
        setActionError('No eligible groups to stop (none running)');
        return;
      }

      const result: any = await backend.bulkStopBots(groupIds);
      if (result.summary?.failed > 0) {
        const failedGroups = Object.entries(result.results || {})
          .filter(([_, r]: [string, any]) => !r.success)
          .map(([gid, r]: [string, any]) => `Group ${gid}: ${r.message}`)
          .join('; ');
        setActionError(`${result.summary.failed} group(s) failed to stop: ${failedGroups}`);
      } else {
        setSelectedGroups(new Set());
      }
    } catch (err: any) {
      const message = err?.body?.detail || err?.message || 'Bulk stop failed';
      setActionError(String(message));
    } finally {
      setBulkActionInProgress(null);
      queryClient.invalidateQueries({ queryKey: ['botStatus'] });
    }
  }, [selectedGroups, botData, backend, queryClient]);

  const handleOpenLogs = useCallback((groupId: string, containerName: string) => {
    setLogsModal({ isOpen: true, groupId, containerName });
  }, []);

  const handleCloseLogs = useCallback(() => {
    setLogsModal({ isOpen: false, groupId: '', containerName: '' });
  }, []);

  const toggleExperiment = useCallback((expId: number) => {
    setCollapsedExperiments((prev) => {
      const next = new Set(prev);
      if (next.has(expId)) {
        next.delete(expId);
      } else {
        next.add(expId);
      }
      return next;
    });
  }, []);

  const handleDeleteExperiment = useCallback((experimentId: number, experimentName: string) => {
    if (window.confirm(`Delete experiment "${experimentName}"?\n\nThe bot groups will become standalone (not deleted).`)) {
      deleteExperimentMutation.mutate(experimentId);
    }
  }, [deleteExperimentMutation]);

  const handleRemoveGroupFromExperiment = useCallback((experimentId: number, groupId: number, groupName: string) => {
    if (window.confirm(`Remove "${groupName}" from this experiment?\n\nThe group will become standalone.`)) {
      removeGroupFromExperimentMutation.mutate({ experimentId, groupId });
    }
  }, [removeGroupFromExperimentMutation]);

  const handleOpenAddToExperiment = useCallback((groupId: number, groupName: string) => {
    setAddToExperimentModal({ isOpen: true, groupId, groupName });
  }, []);

  const handleAddGroupToExperiment = useCallback((experimentId: number) => {
    addGroupToExperimentMutation.mutate({ experimentId, groupId: addToExperimentModal.groupId });
  }, [addGroupToExperimentMutation, addToExperimentModal.groupId]);

  const toggleBotExpanded = useCallback((groupId: string) => {
    setExpandedBots((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const renderBotCard = (groupId: string, bot: BotInfo, experimentId?: number) => {
    const groupUserCount = getGroupUserCount(groupId);
    const currentUserCount = getUserCount(groupId);
    const isInExperiment = experimentId !== undefined;
    const isExpanded = expandedBots.has(groupId);
    const isSelected = selectedGroups.has(groupId);
    
    return (
      <div key={groupId} className={`bot-card ${isExpanded ? 'expanded' : ''} ${isSelected ? 'selected' : ''}`}>
        <button
          type="button"
          className={`group-select-btn ${isSelected ? 'active' : ''}`}
          onClick={() => toggleGroupSelection(groupId)}
          title={isSelected ? 'Deselect group' : 'Select for bulk action'}
          aria-label={isSelected ? `Deselect group ${bot.group_name}` : `Select group ${bot.group_name}`}
          aria-pressed={isSelected}
        />
        <div className="bot-card-content">
        <div className="bot-header" onClick={() => toggleBotExpanded(groupId)}>
          <div className="bot-title">
            <span className="group-name">{bot.group_name}</span>
            <span className="group-id">ID: {bot.group_id}</span>
          </div>
          <div className="bot-header-actions">
            {isInExperiment && (
              <button
                className="action-btn-small unassign-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveGroupFromExperiment(experimentId, bot.group_id, bot.group_name);
                }}
                disabled={removeGroupFromExperimentMutation.isPending}
                title="Remove from experiment (becomes standalone)"
              >
                ↩️ Unassign
              </button>
            )}
            <span 
              className="bot-status-badge"
              style={{ backgroundColor: getStatusColor(bot.is_running) }}
            >
              {bot.is_running 
                ? <>●{bot.users_count ? ` ${bot.users_count}` : ''}</> 
                : bot.status === 'not_found' ? '○' : '◐'}
            </span>
          </div>
        </div>

        {/* Compact summary view */}
        <div className="bot-summary" onClick={() => toggleBotExpanded(groupId)}>
          <div className="bot-summary-stats">
            <div className="bot-summary-stat">
              <span className="label">Personas:</span>
              <span className="value">{groupUserCount}</span>
            </div>
            {bot.is_running && bot.users_count && (
              <div className="bot-summary-stat">
                <span className="label">Active:</span>
                <span className="value">{bot.users_count}</span>
              </div>
            )}
          </div>
          <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
        </div>
        
        {/* Expanded details */}
        <div className="bot-details">
          <div className="detail-row">
            <strong>Container:</strong> {bot.container_name}
          </div>
          <div className="detail-row">
            <strong>Personas in Group:</strong> {groupUserCount}
          </div>
          {bot.users_count && bot.is_running && (
            <div className="detail-row">
              <strong>Active Users:</strong> {bot.users_count}
            </div>
          )}
          {bot.id && (
            <div className="detail-row">
              <strong>ID:</strong> {bot.id}
            </div>
          )}
          {bot.image && (
            <div className="detail-row">
              <strong>Image:</strong> {bot.image}
            </div>
          )}
          {bot.created && (
            <div className="detail-row">
              <strong>Created:</strong> {formatDate(bot.created)}
            </div>
          )}
        </div>

        {/* Commands only shown when expanded */}
        {isExpanded && (
          <div className="bot-commands">
            {!bot.is_running && (
              <div className="user-count-input">
                <label htmlFor={`users-${groupId}`}>
                  Users to run (max {groupUserCount}):
                </label>
                <input
                  id={`users-${groupId}`}
                  type="number"
                  min="1"
                  max={Math.max(groupUserCount, 1)}
                  value={currentUserCount}
                  onChange={(e: any) => setUserCount(groupId, Math.max(1, Math.min(parseInt(e.target.value) || 1, groupUserCount)))}
                  disabled={startBotMutation.isPending}
                  className="user-count-field"
                  onClick={(e) => e.stopPropagation()}
                />
                {groupUserCount === 0 && (
                  <small style={{ color: '#f85149', display: 'block', marginTop: '0.25rem', fontSize: '0.7rem' }}>
                    ⚠️ No personas in this group
                  </small>
                )}
              </div>
            )}
            <div className="command-buttons">
              <button 
                className="action-btn start-btn"
                onClick={(e) => { e.stopPropagation(); handleStartBot(groupId); }}
                disabled={bot.is_running || startBotMutation.isPending || groupUserCount === 0}
                title={
                  bot.is_running 
                    ? 'Bot is already running' 
                    : groupUserCount === 0
                      ? 'No personas available in this group'
                      : `Start bot with ${currentUserCount} user${currentUserCount > 1 ? 's' : ''}`
                }
              >
                {startBotMutation.isPending && startBotMutation.variables?.groupId === groupId 
                  ? '⏳' 
                  : bot.is_running 
                    ? '✅ Running' 
                    : groupUserCount === 0
                      ? '❌'
                      : `▶️ Start`}
              </button>
              <button 
                className="action-btn stop-btn"
                onClick={(e) => { e.stopPropagation(); handleStopBot(groupId); }}
                disabled={!bot.is_running || stopBotMutation.isPending}
                title={!bot.is_running ? 'Bot is not running' : 'Stop bot'}
              >
                {stopBotMutation.isPending && stopBotMutation.variables === groupId 
                  ? '⏳' 
                  : '⏹️ Stop'}
              </button>
              <button 
                className="action-btn logs-btn"
                onClick={(e) => { e.stopPropagation(); handleOpenLogs(groupId, bot.container_name); }}
                disabled={bot.status === "not_found"}
                title={bot.status === "not_found" ? "Container not found" : "View logs"}
              >
                📋 Logs
              </button>
              {!isInExperiment && experiments && (experiments as Experiment[]).length > 0 && (
                <button
                  className="action-btn add-to-exp-btn"
                  onClick={(e) => { e.stopPropagation(); handleOpenAddToExperiment(bot.group_id, bot.group_name); }}
                  title="Add to experiment"
                >
                  🧪 Add
                </button>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bot-status">
        <header className="page-header">
          <h1><img src="/resources/tiktok-logo.svg" alt="TikTok" style={{ width: '48px', height: '48px', marginRight: '8px' }} /> TikTok Bot Manager</h1>
          <button onClick={() => refetch()} className="refresh-btn">
            🔄 Refresh
          </button>
        </header>
        <main className="page-main">
          <div className="loading">Loading bot status...</div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bot-status">
        <header className="page-header">
          <h1><img src="/resources/tiktok-logo.svg" alt="TikTok" style={{ width: '48px', height: '48px', marginRight: '8px' }} /> TikTok Bot Status</h1>
          <button onClick={() => refetch()} className="refresh-btn">
            🔄 Refresh
          </button>
        </header>
        <main className="page-main">
          <div className="error">
            {error.message}
            <br />
            <small>Make sure the backend is running and accessible</small>
          </div>
        </main>
      </div>
    );
  }

  if (!botData) {
    return (
      <div className="bot-status">
        <header className="page-header">
          <h1><img src="/resources/tiktok-logo.svg" alt="TikTok" style={{ width: '48px', height: '48px', marginRight: '8px' }} /> TikTok Bot Status</h1>
          <button onClick={() => refetch()} className="refresh-btn">
            🔄 Refresh
          </button>
        </header>
        <main className="page-main">
          <div className="loading">No data available</div>
        </main>
      </div>
    );
  }

  const experimentCount = experimentBotsList.length;
  const standaloneCount = Object.keys(standaloneBots).length;

  return (
    <div className="bot-status">
      <header className="page-header">
        <h1><img src="/resources/tiktok-logo.svg" alt="TikTok" style={{ width: '48px', height: '48px', marginRight: '8px' }} /> TikTok Bot Status</h1>
        <button onClick={() => refetch()} className="refresh-btn">
          🔄 Refresh
        </button>
      </header>

      <main className="page-main">
        {actionError && (
          <div className="error">
            <strong>Action Error:</strong> {actionError}
            <button 
              onClick={() => setActionError(null)}
              style={{ 
                float: 'right', 
                background: 'none', 
                border: 'none', 
                color: '#c62828', 
                cursor: 'pointer',
                fontSize: '1.2rem'
              }}
            >
              ×
            </button>
          </div>
        )}
        
        <div className="bot-container">
          <div className="summary-card">
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-number">{botData.summary.running}</span>
                <span className="stat-label">Running</span>
              </div>
              <div className="stat">
                <span className="stat-number">{botData.summary.stopped}</span>
                <span className="stat-label">Stopped</span>
              </div>
              <div className="stat">
                <span className="stat-number">{botData.summary.total_bots}</span>
                <span className="stat-label">Total</span>
              </div>
              <div className="stat">
                <span className="stat-number">{experimentCount}</span>
                <span className="stat-label">Experiments</span>
              </div>
            </div>
          </div>

          {/* Bulk Action Bar */}
          {botData?.bots && Object.keys(botData.bots).length > 0 && (
            <div className="bulk-action-bar">
              <div className="bulk-select-controls">
                <button
                  className="action-btn-small"
                  onClick={() => selectAllGroups(botData.bots)}
                >
                  Select All
                </button>
                <button
                  className="action-btn-small"
                  onClick={clearSelection}
                  disabled={selectedGroups.size === 0}
                >
                  Clear
                </button>
                {selectedGroups.size > 0 && (
                  <span className="bulk-selection-count">
                    {selectedGroups.size} group{selectedGroups.size !== 1 ? 's' : ''} selected
                  </span>
                )}
              </div>
              <div className="bulk-action-buttons">
                <button
                  className={`action-btn ${selectedGroups.size > 0 ? 'start-btn' : ''}`}
                  onClick={handleBulkStart}
                  disabled={selectedGroups.size === 0 || bulkActionInProgress !== null}
                >
                  {bulkActionInProgress === 'start' ? '⏳ Starting...' : '▶️ Start Selected'}
                </button>
                <button
                  className={`action-btn ${selectedGroups.size > 0 ? 'stop-btn' : ''}`}
                  onClick={handleBulkStop}
                  disabled={selectedGroups.size === 0 || bulkActionInProgress !== null}
                >
                  {bulkActionInProgress === 'stop' ? '⏳ Stopping...' : '⏹️ Stop Selected'}
                </button>
              </div>
            </div>
          )}

          {/* Experiments Section */}
          {experimentCount > 0 && (
            <div className="experiments-section">
              <h2 className="section-title">
                <span className="section-icon">🧪</span>
                Experiments
              </h2>
              
              {experimentBotsList.map(({ experiment, bots }) => {
                const isCollapsed = collapsedExperiments.has(experiment.experimentId);
                const runningCount = Object.values(bots).filter((b) => b.is_running).length;
                const totalCount = Object.keys(bots).length;
                
                return (
                  <div key={experiment.experimentId} className="experiment-group">
                    <div 
                      className="experiment-header"
                      onClick={() => toggleExperiment(experiment.experimentId)}
                    >
                      <div className="experiment-info">
                        <span className="experiment-icon">🧪</span>
                        <div className="experiment-details">
                          <h3 className="experiment-name">{experiment.experimentName}</h3>
                          <div className="experiment-meta">
                            <span className="experiment-dates">
                              {experiment.experimentStartTime && (
                                <>📅 {new Date(experiment.experimentStartTime).toLocaleDateString()}</>
                              )}
                              {experiment.experimentEndTime && (
                                <> → {new Date(experiment.experimentEndTime).toLocaleDateString()}</>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="experiment-stats">
                        <span className="running-badge" style={{ 
                          background: runningCount > 0 ? 'rgba(63, 185, 80, 0.15)' : 'rgba(248, 81, 73, 0.15)',
                          color: runningCount > 0 ? '#3fb950' : '#f85149'
                        }}>
                          {runningCount}/{totalCount} running
                        </span>
                        <button
                          className="action-btn-small delete-exp-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteExperiment(experiment.experimentId, experiment.experimentName);
                          }}
                          disabled={deleteExperimentMutation.isPending}
                          title="Delete experiment (groups become standalone)"
                        >
                          🗑️
                        </button>
                        <span className="expand-icon">{isCollapsed ? '▶' : '▼'}</span>
                      </div>
                    </div>
                    
                    {!isCollapsed && (
                      <div className="experiment-bots">
                        <div className="bots-grid">
                          {Object.entries(bots).map(([groupId, bot]) => renderBotCard(groupId, bot, experiment.experimentId))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Standalone Groups Section */}
          {standaloneCount > 0 && (
            <div className="standalone-section">
              <h2 className="section-title">
                <span className="section-icon">📦</span>
                Standalone Bot Groups
              </h2>
              <div className="bots-grid">
                {Object.entries(standaloneBots).map(([groupId, bot]) => renderBotCard(groupId, bot))}
              </div>
            </div>
          )}

          {experimentCount === 0 && standaloneCount === 0 && (
            <div className="empty-state-container">
              <div className="empty-state">
                <span className="empty-icon">🤖</span>
                <h3>No Bot Groups Found</h3>
                <p>Create a bot group or experiment to get started</p>
              </div>
            </div>
          )}
        </div>
      </main>
      
      <BotsLogsModal
        isOpen={logsModal.isOpen}
        onClose={handleCloseLogs}
        groupId={logsModal.groupId}
        containerName={logsModal.containerName}
      />

      {/* Add to Experiment Modal */}
      {addToExperimentModal.isOpen && (
        <div className="modal-overlay" onClick={() => setAddToExperimentModal({ isOpen: false, groupId: 0, groupName: '' })}>
          <div className="modal-content add-to-exp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add to Experiment</h3>
              <button 
                className="modal-close"
                onClick={() => setAddToExperimentModal({ isOpen: false, groupId: 0, groupName: '' })}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Add <strong>"{addToExperimentModal.groupName}"</strong> to an experiment:
              </p>
              <div className="experiment-list">
                {experiments && (experiments as Experiment[]).map((exp) => (
                  <button
                    key={exp.experimentId}
                    className="experiment-option"
                    onClick={() => handleAddGroupToExperiment(exp.experimentId)}
                    disabled={addGroupToExperimentMutation.isPending || exp.groupIds.includes(addToExperimentModal.groupId)}
                  >
                    <span className="exp-icon">🧪</span>
                    <span className="exp-name">{exp.experimentName}</span>
                    {exp.groupIds.includes(addToExperimentModal.groupId) && (
                      <span className="already-added">Already added</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BotStatus;
